import { useEffect, useState } from 'react';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
        }`}
      >
        <div className="px-8 py-6">
          <div className="flex items-center">
            <img 
              src="https://public.readdy.ai/ai/img_res/77078866-0852-4f54-b649-d5bf3e4ab8a9.png" 
              alt="ALPAN SH.P.K. Logo" 
              className="h-12 w-auto"
            />
            <span className={`ml-4 text-2xl font-semibold transition-colors ${isScrolled ? 'text-gray-900' : 'text-white'}`}>
              ALPAN SH.P.K.
            </span>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        
        {/* Abstract geometric shapes */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-20 w-96 h-96 bg-purple-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 left-20 w-96 h-96 bg-blue-500 rounded-full blur-3xl"></div>
        </div>

        <div className="container mx-auto px-8 relative z-10">
          <div className="max-w-4xl">
            <h1 className="text-7xl lg:text-8xl font-extrabold text-white mb-8 leading-tight tracking-wide">
              Crafting Premium<br />
              iOS Experiences<br />
              for Modern Users
            </h1>
            <p className="text-xl text-gray-300 mb-16 max-w-2xl leading-relaxed">
              Expert team developing cutting-edge VPN and Cleaner applications that elevate security and performance on iOS devices.
            </p>
          </div>
          
          <div className="mt-32">
            <h2 className="text-8xl lg:text-9xl font-black text-white tracking-tight">
              INNOVATION<br />MEETS SECURITY
            </h2>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-32 bg-[#F5F3EF]">
        <div className="container mx-auto px-8">
          <div className="mb-12">
            <span className="text-xs uppercase tracking-widest text-gray-600">WHO WE ARE</span>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div></div>
            <div>
              <h2 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-8 leading-tight">
                We are ALPAN SH.P.K.<br />
                A dedicated team<br />
                pushing iOS boundaries
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-12">
                Our expertise lies in developing sophisticated applications that address real user needs. From advanced VPN solutions ensuring privacy to intelligent Cleaner tools optimizing device performance, we combine technical excellence with intuitive design.
              </p>
              
              <div className="grid grid-cols-5 gap-10">
                <div className="col-span-2">
                  <img 
                    src="https://public.readdy.ai/ai/img_res/4f3a71598c0e7afb676d1f20f4a614df.jpg" 
                    alt="Code Visualization" 
                    className="w-full h-[500px] object-cover rounded-3xl shadow-lg"
                  />
                </div>
                <div className="col-span-3">
                  <img 
                    src="https://readdy.ai/api/search-image?query=modern%20iOS%20mobile%20application%20interface%20mockup%20displayed%20on%20elegant%20smartphone%20screen%20with%20clean%20minimalist%20design%20featuring%20gradient%20purple%20and%20blue%20colors%20warm%20ambient%20lighting%20professional%20product%20photography%20style%20showcasing%20premium%20app%20development%20quality%20with%20soft%20shadows%20and%20contemporary%20aesthetic&width=600&height=500&seq=alpan-ios-mockup&orientation=landscape" 
                    alt="iOS Interface Mockups" 
                    className="w-full h-[500px] object-cover rounded-3xl shadow-lg"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-32 bg-white">
        <div className="container mx-auto px-8">
          <h2 className="text-6xl font-bold text-center mb-20 leading-tight">
            Powerful Solutions for <span className="text-teal-500">iOS Security</span><br />
            and <span className="text-teal-500">Performance</span>
          </h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-7xl mx-auto">
            {/* VPN Card - Large Vertical */}
            <div className="relative h-[600px] rounded-[32px] overflow-hidden shadow-xl group cursor-pointer">
              <img 
                src="https://readdy.ai/api/search-image?query=premium%20VPN%20security%20shield%20protection%20concept%20with%20glowing%20blue%20and%20purple%20gradient%20background%20featuring%20abstract%20digital%20network%20connections%20encrypted%20data%20streams%20and%20cybersecurity%20elements%20modern%20tech%20aesthetic%20with%20depth%20and%20dimension%20professional%20quality%20suitable%20for%20mobile%20app%20showcase&width=600&height=800&seq=alpan-vpn-shield&orientation=portrait" 
                alt="VPN Protection" 
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-10">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center">
                    <i className="ri-shield-check-line text-4xl text-teal-400"></i>
                  </div>
                  <h3 className="text-3xl font-bold text-white">VPN Protection</h3>
                </div>
                <p className="text-gray-200 text-lg leading-relaxed">
                  Advanced encryption technology ensuring your privacy and security across all networks. Browse with confidence knowing your data is protected.
                </p>
              </div>
            </div>
            
            {/* Right Column - Two Stacked Cards */}
            <div className="flex flex-col gap-8">
              {/* Cleaner Card */}
              <div className="relative h-[284px] rounded-[32px] overflow-hidden shadow-xl bg-[#E8F5E9] group cursor-pointer">
                <img 
                  src="https://readdy.ai/api/search-image?query=clean%20minimalist%20iOS%20mobile%20app%20interface%20showing%20storage%20cleaner%20tool%20with%20soft%20sage%20green%20background%20featuring%20simple%20circular%20progress%20indicators%20and%20elegant%20typography%20displaying%20storage%20optimization%20metrics%20modern%20flat%20design%20with%20plenty%20of%20white%20space%20and%20gentle%20shadows%20professional%20app%20screenshot%20style&width=700&height=400&seq=alpan-cleaner-ui&orientation=landscape" 
                  alt="Cleaner Tool" 
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 flex items-center justify-center">
                      <i className="ri-delete-bin-line text-3xl text-teal-400"></i>
                    </div>
                    <h3 className="text-2xl font-bold text-white">Cleaner Tool</h3>
                  </div>
                  <p className="text-gray-200 leading-relaxed">
                    Intelligent optimization that frees up space and boosts performance effortlessly.
                  </p>
                </div>
              </div>
              
              {/* Performance Card */}
              <div className="relative h-[284px] rounded-[32px] overflow-hidden shadow-xl bg-[#FFE8EC] group cursor-pointer">
                <img 
                  src="https://readdy.ai/api/search-image?query=modern%20performance%20metrics%20dashboard%20visualization%20with%20elegant%20graphs%20and%20data%20charts%20on%20soft%20rose%20pink%20background%20showing%20mobile%20device%20optimization%20statistics%20clean%20minimalist%20interface%20design%20with%20smooth%20gradient%20lines%20and%20contemporary%20typography%20professional%20analytics%20presentation%20style%20with%20gentle%20colors&width=700&height=400&seq=alpan-performance&orientation=landscape" 
                  alt="Performance Metrics" 
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 flex items-center justify-center">
                      <i className="ri-speed-line text-3xl text-teal-400"></i>
                    </div>
                    <h3 className="text-2xl font-bold text-white">Peak Performance</h3>
                  </div>
                  <p className="text-gray-200 leading-relaxed">
                    Real-time monitoring and optimization for maximum device efficiency.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-32 bg-[#FAFAFA]">
        <div className="container mx-auto px-8">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-5xl font-bold text-center mb-20 text-gray-900">
              Why Choose Our Applications
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
              {/* Feature 1 */}
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center bg-teal-500 rounded-2xl">
                  <i className="ri-lock-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold mb-4 text-gray-900">Military-Grade Encryption</h3>
                <p className="text-gray-600 leading-relaxed">
                  Your data is protected with the highest level of encryption standards, ensuring complete privacy and security.
                </p>
              </div>
              
              {/* Feature 2 */}
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center bg-teal-500 rounded-2xl">
                  <i className="ri-flashlight-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold mb-4 text-gray-900">Lightning Fast</h3>
                <p className="text-gray-600 leading-relaxed">
                  Optimized performance ensures your device runs smoothly without compromising speed or functionality.
                </p>
              </div>
              
              {/* Feature 3 */}
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center bg-teal-500 rounded-2xl">
                  <i className="ri-smartphone-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold mb-4 text-gray-900">Intuitive Design</h3>
                <p className="text-gray-600 leading-relaxed">
                  Clean, user-friendly interfaces that make powerful features accessible to everyone.
                </p>
              </div>
              
              {/* Feature 4 */}
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center bg-teal-500 rounded-2xl">
                  <i className="ri-refresh-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold mb-4 text-gray-900">Regular Updates</h3>
                <p className="text-gray-600 leading-relaxed">
                  Continuous improvements and new features to keep your apps at the cutting edge of technology.
                </p>
              </div>
              
              {/* Feature 5 */}
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center bg-teal-500 rounded-2xl">
                  <i className="ri-customer-service-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold mb-4 text-gray-900">Expert Support</h3>
                <p className="text-gray-600 leading-relaxed">
                  Dedicated team ready to assist you with any questions or concerns you may have.
                </p>
              </div>
              
              {/* Feature 6 */}
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center bg-teal-500 rounded-2xl">
                  <i className="ri-global-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold mb-4 text-gray-900">Global Reach</h3>
                <p className="text-gray-600 leading-relaxed">
                  Trusted by users worldwide for reliable performance and exceptional quality.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative min-h-[600px] flex items-center overflow-hidden">
        <img 
          src="https://readdy.ai/api/search-image?query=abstract%20digital%20network%20visualization%20with%20cool%20blue%20tones%20and%20beautiful%20bokeh%20light%20effects%20showing%20interconnected%20nodes%20and%20flowing%20data%20streams%20creating%20depth%20and%20technology%20atmosphere%20with%20soft%20glowing%20particles%20and%20elegant%20blur%20professional%20panoramic%20composition%20suitable%20for%20tech%20company%20background&width=1920&height=800&seq=alpan-cta-bg&orientation=landscape" 
          alt="Digital Network" 
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30"></div>
        
        <div className="container mx-auto px-8 relative z-10">
          <div className="max-w-6xl">
            <h2 className="text-7xl font-bold text-white mb-32 lowercase leading-tight" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.3)' }}>
              ready to enhance<br />
              your ios experience
            </h2>
            
            <div className="flex items-end justify-between">
              <div className="inline-flex items-center bg-white rounded-full h-14 pr-2 pl-8 gap-4 hover:shadow-xl transition-shadow cursor-pointer">
                <span className="text-gray-900 font-medium whitespace-nowrap">Get Started</span>
                <div className="w-10 h-10 rounded-full bg-gray-900 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-white text-xl"></i>
                </div>
              </div>
              
              <p className="text-white text-xl font-light leading-relaxed max-w-md" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.3)' }}>
                Join thousands of users trusting<br />
                ALPAN SH.P.K. for their iOS needs
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact & Footer */}
      <footer id="contact" className="bg-[#F8F6F3] py-20">
        <div className="container mx-auto px-8">
          {/* Footer Navigation Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-32">
            {/* Products */}
            <div>
              <h4 className="text-xs uppercase tracking-widest text-gray-600 mb-4">PRODUCTS</h4>
              <div className="w-10 h-px bg-gray-400 mb-6"></div>
              <ul className="space-y-4">
                <li className="text-gray-800">VPN Application</li>
                <li className="text-gray-800">Cleaner Tool</li>
                <li className="text-gray-800">Security Suite</li>
              </ul>
            </div>
            
            {/* Company */}
            <div>
              <h4 className="text-xs uppercase tracking-widest text-gray-600 mb-4">COMPANY</h4>
              <div className="w-10 h-px bg-gray-400 mb-6"></div>
              <ul className="space-y-4">
                <li className="text-gray-800">About ALPAN SH.P.K.</li>
                <li className="text-gray-800">Our Team</li>
                <li className="text-gray-800">Technology</li>
              </ul>
            </div>
            
            {/* Support */}
            <div>
              <h4 className="text-xs uppercase tracking-widest text-gray-600 mb-4">SUPPORT</h4>
              <div className="w-10 h-px bg-gray-400 mb-6"></div>
              <ul className="space-y-4">
                <li className="text-gray-800">Help Center</li>
                <li className="text-gray-800">Documentation</li>
                <li className="text-gray-800">Privacy Policy</li>
              </ul>
            </div>
            
            {/* Platform */}
            <div>
              <h4 className="text-xs uppercase tracking-widest text-gray-600 mb-4">PLATFORM</h4>
              <div className="w-10 h-px bg-gray-400 mb-6"></div>
              <ul className="space-y-4">
                <li className="text-gray-800">iOS Requirements</li>
                <li className="text-gray-800">Compatibility</li>
                <li className="text-gray-800">Updates</li>
              </ul>
            </div>
          </div>
          
          {/* Bottom Section */}
          <div className="flex flex-col lg:flex-row justify-between items-start gap-16">
            {/* Left - Brand */}
            <div>
              <h3 className="text-8xl font-black text-gray-900 leading-none mb-2">ALPAN</h3>
              <h3 className="text-8xl font-black text-gray-900 leading-none mb-4">SH.P.K.</h3>
              <div className="flex items-center gap-4">
                <div className="w-20 h-0.5 bg-teal-500"></div>
                <span className="text-sm text-gray-500">Est. 2024</span>
              </div>
            </div>
            
            {/* Right - Contact & Copyright */}
            <div className="flex flex-col items-end gap-8">
              <div className="text-sm text-gray-600">
                © 2025 ALPAN SH.P.K. All rights reserved.
              </div>
              
              {/* Contact Information */}
              <div className="text-right">
                <h4 className="text-xs uppercase tracking-widest text-gray-500 mb-4">CONTACT</h4>
                
                <div className="space-y-2 text-gray-800">
                  <div className="flex items-start justify-end gap-2">
                    <div className="w-4 h-4 flex items-center justify-center mt-1">
                      <i className="ri-map-pin-line text-teal-500"></i>
                    </div>
                    <div className="text-right">
                      <strong>Address:</strong> LAGJJA XHEVDET NEPRAVISHTA, RRUGA E BERATIT
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-end gap-2">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-building-line text-teal-500"></i>
                    </div>
                    <div><strong>City:</strong> LUSHNJE</div>
                  </div>
                  
                  <div className="flex items-center justify-end gap-2">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-mail-line text-teal-500"></i>
                    </div>
                    <div><strong>Zip:</strong> 9000</div>
                  </div>
                  
                  <div className="flex items-center justify-end gap-2">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-global-line text-teal-500"></i>
                    </div>
                    <div><strong>Country:</strong> Albania</div>
                  </div>
                  
                  <div className="h-4"></div>
                  
                  <div className="flex items-center justify-end gap-2">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-mail-send-line text-teal-500"></i>
                    </div>
                    <div><strong>Email:</strong> help@alpanshpk.com</div>
                  </div>
                  
                  <div className="flex items-center justify-end gap-2">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-phone-line text-teal-500"></i>
                    </div>
                    <div><strong>Phone:</strong> +1 8084006021</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
